import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { KnowledgeApi } from "~/functions/api/knowledge";
import type { RootState } from "~/store";

interface UseKnowledgeCountReturn {
  count: number;
  totalCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useKnowledgeCount = (documentType: string): UseKnowledgeCountReturn => {
  const [count, setCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth) as any;
  const knowledgeApi = useMemo(() => new KnowledgeApi(), []);

  const fetchKnowledgeCount = useCallback(async () => {
    if (!currentProject?.id || !currentUser?.organization_id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 承認済みのナレッジを取得
      const approvedData = await knowledgeApi.getByStatus(
        currentProject.id,
        currentUser.organization_id,
        "approved",
      );

      if (approvedData) {
        // データを配列に変換
        const approvedArray = Array.isArray(approvedData) ? approvedData : [approvedData];

        // 指定されたドキュメントタイプの全ナレッジ
        const filteredAll = approvedArray.filter((item) => item.type === documentType);

        // 指定されたドキュメントタイプで有効なナレッジ
        const filteredActive = filteredAll.filter((item) => item.isActive);

        setCount(filteredActive.length);
        setTotalCount(filteredAll.length);
      } else {
        setCount(0);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch knowledge count:", error);
      setError("ナレッジ数の取得に失敗しました");
      setCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, currentUser?.organization_id, documentType, knowledgeApi]);

  useEffect(() => {
    fetchKnowledgeCount();
  }, [fetchKnowledgeCount]);

  const refetch = () => {
    fetchKnowledgeCount();
  };

  return {
    count,
    totalCount,
    loading,
    error,
    refetch,
  };
};
